import React, { useState, useEffect, useContext } from "react";
import { useLocation } from "react-router-dom";
import {
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarExport,
} from "@mui/x-data-grid";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { LicenseInfo } from "@mui/x-license-pro";
import { Tooltip, Box } from "@mui/material";
import Header from "../layout/Header";
import BaseCommissionAdvancedSearch from "./BaseCommissionAdvancedSearch";
import FullPageGridContainer from "../layout/FullPageGridContainer";
import useFetch from "../../hooks/useFetch";
import ErrorMessage from "../pages/ErrorMessage";
import InternalLink from "../routing/InternalLink";
import { AuthContext } from "../../services/Authentication";
import usePermissions from "../../hooks/usePermissions";

LicenseInfo.setLicenseKey(process.env.REACT_APP_MUI_LICENSE_KEY);

let userTitle = "Unknown";

const renderCell = (params) => (
  <Tooltip title={params.value || ""}>
    <span>{params.formattedValue}</span>
  </Tooltip>
);

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

const usdPrice = {
  type: "number",
  valueFormatter: ({ value }) => currencyFormatter.format(Number(value)),
};

const CustomToolbar = ({ currEndpoint }) => {
  //console.log("Rendering CustomToolbar with currEndpoint:", currEndpoint);

  if (!currEndpoint) {
    return null; // Or render a loading state, e.g. "Loading..."
  }

  return (
    <GridToolbarContainer className="flex justify-between">
      <Box>
        <GridToolbarColumnsButton />
        <GridToolbarExport printOptions={{ disableToolbarButton: true }} />
      </Box>
      <BaseCommissionAdvancedSearch endpoint={currEndpoint} />
    </GridToolbarContainer>
  );
};

const CustomFooter = (props) => (
  <Box sx={{ padding: "16px", display: "flex", flexDirection: "row-reverse" }}>
    {props.totalCommissionString}
  </Box>
);

// Function to build the hierarchy
const buildHierarchyData = (commissionResults, user, isAdmin) => {
  console.log("USER", user.permissions);
  const userName = user.firstname + " " + user.lastname;
  const repData = commissionResults.find(
    (record) => record.repName === userName
  );
  userTitle = repData ? repData.repTitle : "Unknown";
  if (isAdmin) {
    userTitle = "Admin";
  }
  const managers = {};
  const teamLeads = {};
  const resultOrder = [];

  // Helper function to check for valid names
  const isValidName = (name) => name && name.trim() !== "";

  // First pass: Populate managers and team leads
  commissionResults.forEach((rep) => {
    const { managerName, teamLeadName, repName, repTitle } = rep;

    if (repTitle === "Manager" && isValidName(repName)) {
      if (!managers[repName]) {
        managers[repName] = { name: repName, salesReps: [], teamLeads: [] };
      }
    } else if (repTitle === "Team Lead" && isValidName(repName)) {
      if (!teamLeads[repName]) {
        teamLeads[repName] = { name: repName, salesReps: [] };
      }
    }
  });

  // Second pass: Link team leads to managers and sales reps to team leads or managers
  commissionResults.forEach((rep) => {
    const { managerName, teamLeadName, repName, repTitle } = rep;

    if (repTitle === "Team Lead" && isValidName(repName)) {
      if (isValidName(managerName) && managers[managerName]) {
        managers[managerName].teamLeads.push(teamLeads[repName]);
      }
    } else if (repTitle === "Sales Rep" && isValidName(repName)) {
      if (isValidName(teamLeadName) && teamLeads[teamLeadName]) {
        // Link sales rep to team lead
        teamLeads[teamLeadName].salesReps.push(repName);
      } else if (isValidName(managerName) && managers[managerName]) {
        // Link sales rep directly to manager
        managers[managerName].salesReps.push(repName);
      }
    }
  });

  // Filter based on the user's title
  const filterByUserTitle = (entity) => {
    if (userTitle === "Admin") {
      return true; // Admin sees everything
    }
    if (userTitle === "Manager") {
      return (
        entity.name == userName ||
        (entity.teamLeads &&
          entity.teamLeads.some((teamLead) => teamLead.name === userName)) ||
        (teamLeads[entity.name]?.salesReps || []).includes(userName)
      );
    }
    if (userTitle === "Team Lead") {
      return (
        entity.name == userName ||
        (teamLeads[entity.name]?.salesReps || []).includes(userName)
      );
    }
    if (userTitle === "Sales Rep") {
      return entity.name == userName;
    }
    return false;
  };

  // Build the ordered list
  Object.values(managers).forEach((manager) => {
    if (isValidName(manager.name) && filterByUserTitle(manager)) {
      resultOrder.push(manager.name); // Add manager's name
    }

    // Add sales reps directly under the manager (no team lead)
    manager.salesReps.forEach((salesRep) => {
      if (isValidName(salesRep)) {
        resultOrder.push(salesRep); // Add sales rep's name directly under manager
      }
    });

    // Add team leads and their sales reps
    manager.teamLeads.forEach((teamLead) => {
      if (isValidName(teamLead.name) && filterByUserTitle(teamLead)) {
        resultOrder.push(teamLead.name); // Add team lead's name
      }
      teamLead.salesReps.forEach((salesRep) => {
        if (isValidName(salesRep)) {
          resultOrder.push(salesRep); // Add sales rep's name under team lead
        }
      });
    });
  });

  return resultOrder;
};

const BaseCommissionReport = ({ endpoint }) => {
  //console.log("First enpoint", endpoint)
  const [sales, setSales] = useState([]);
  const [salesRepSummary, setSalesRepSummary] = useState([]);
  const location = useLocation();
  const params = Object.fromEntries(new URLSearchParams(location.search));
  const { state: authState } = useContext(AuthContext);
  const permissions = usePermissions();
  const [totalCommissionString, setTotalCommissionString] = useState("");
  const [currEndpoint, setCurrentEnpoint] = useState(endpoint);

  const netsuiteColumns = [
    "closedate",
    "trandate",
    "tranid",
    "internalid",
    "entity",
    "quantity",
    "item",
    "memo",
    "partner",
    "salesrep",
    "amount",
    "otherrefnum",
  ];

  const columns = [
    { field: "id", headerName: "id", hide: true },
    { field: "closedate", headerName: "Date Closed", minWidth: 100 },
    {
      field: "document_number",
      headerName: "Document Number",
      minWidth: 100,
      renderCell: (params) =>
        /^INV[0-9]+$/i.test(params?.row?.document_number) ? (
          <InternalLink to={`/invoices/${params?.row?.internalid}`}>
            {params?.row?.document_number}
          </InternalLink>
        ) : (
          params?.row?.document_number
        ),
    },
    {
      renderCell,
      field: "otherrefnum",
      headerName: "LE-#",
      description: "",
      minWidth: 100,
      flex: 1,
    },
    {
      renderCell,
      field: "customer",
      headerName: "Customer",
      description: "",
      minWidth: 50,
      flex: 1,
    },
    {
      field: "amount",
      headerName: "Amount",
      description: "Amount invoice line",
      minWidth: 100,
      ...usdPrice,
    },
  ];

  columns.push({
    renderCell,
    field: "isr",
    headerName: "ISR",
    description: "Inside sales rep in Netsuite",
    minWidth: 175,
  });

  if (permissions.canViewAllISRCommission) {
    columns.push({
      renderCell,
      field: "team_lead",
      headerName: "Team Lead",
      minWidth: 175,
    });

    //netsuiteColumns.push("salesrep.supervisor");
  }

  if (permissions.canViewAllISRCommission) {
    columns.push({
      renderCell,
      field: "manager",
      headerName: "Manager",
      minWidth: 175,
    });

    //netsuiteColumns.push("salesrep.supervisor");
  }

  columns.push({
    renderCell,
    field: "isr_commission",
    headerName: "ISR Commission",
    description: "Inside sales rep commission",
    minWidth: 175,
    ...usdPrice,
  });

  if (userTitle != "Unknown" && userTitle != "Sales Rep") {
    columns.push({
      renderCell,
      field: "teamlead_commission",
      headerName: "Team Lead Commission",
      description: "Team Lead rep commission",
      minWidth: 175,
      ...usdPrice,
    });
  }
  if (
    userTitle != "Unknown" &&
    userTitle != "Sales Rep" &&
    userTitle != "Team Lead"
  ) {
    columns.push({
      renderCell,
      field: "manager_commission",
      headerName: "Manager Commission",
      description: "Manager rep commission",
      minWidth: 175,
      ...usdPrice,
    });
  }

  const netsuiteFilters = [];
  if (params.salesrep)
    netsuiteFilters.push("AND", ["salesrep", "is", params.salesrep]);
  if (params.supervisor)
    netsuiteFilters.push("AND", [
      "salesrep.supervisor",
      "is",
      params.supervisor,
    ]);
  if (params.partner)
    netsuiteFilters.push("AND", ["partner", "is", params.partner]);
  if (params.customer)
    netsuiteFilters.push("AND", [
      "customer.altname",
      "contains",
      params.customer,
    ]);

  const invoiceFilters = [...netsuiteFilters];
  const creditMemoFilters = [...netsuiteFilters];

  if (params.startdate && params.enddate) {
    invoiceFilters.push("AND", [
      "formuladate: CASE WHEN {type}='Invoice' THEN {closedate} ELSE {trandate} END",
      "within",
      params.startdate,
      params.enddate,
    ]);
    creditMemoFilters.push("AND", [
      "systemnotes.date",
      "within",
      params.startdate,
      params.enddate,
    ]);
  }

  let url = `${
    process.env.REACT_APP_BASE_URL
  }/api/reports/commission/${endpoint}/?results=${JSON.stringify(
    netsuiteColumns
  )}`;

  if (netsuiteFilters) {
    url += `&invoiceParams=${JSON.stringify(
      invoiceFilters
    )}&creditMemoParams=${JSON.stringify(creditMemoFilters)}`;
  }

  const { isLoading, error, data: json } = useFetch({ url });

  useEffect(() => {
    if (!json) return;

    const userName =
      authState?.user?.firstname + " " + authState?.user?.lastname;

    console.log("JSON", json);

    const {
      invoices: { result: invoiceResult, data: invoiceData = [] },
      creditMemos: { result: creditMemoResult, data: creditMemoData = [] },
      commissionResults,
    } = json;

    console.log("commissionResults", commissionResults);

    const hierarchyData = buildHierarchyData(
      commissionResults,
      authState?.user,
      permissions.isAdmin
    );
    console.log("hierarchyData", hierarchyData);

    if (invoiceResult !== "success" || creditMemoResult !== "success") return;

    const data = [...invoiceData, ...creditMemoData];

    console.log("data", data);

    const commissionLookup = commissionResults.reduce((acc, result) => {
      // console.log("teamLeadName", result.teamLeadName);

      // console.log("managerName", result.managerName);
      acc[result.repName] = {
        teamLeadName: result.teamLeadName,
        managerName: result.managerName,
      };
      return acc;
    }, {});

    // Map the sales data and add team lead and manager names
    const mappedSales = data.map(
      (
        {
          internalid: [{ value: internalid } = {}] = [],
          entity: [{ text: customer } = {}] = [],
          closedate,
          trandate,
          tranid: document_number,
          memo: description,
          salesrep: [{ text: isr } = {}] = [],
          "salesrep.supervisor": [{ text: isr_supervisor } = {}] = [],
          amount,
          otherrefnum,
          customerCommissionRate,
        },
        index
      ) => {
        // Get team lead and manager names from the lookup object
        const { teamLeadName, managerName } = commissionLookup[isr] || {};

        return {
          id: index,
          internalid,
          document_number,
          customer,
          isr,
          isr_supervisor,
          closedate: closedate || trandate,
          amount,
          description,
          otherrefnum,
          isr_commission:
            (customerCommissionRate.salesRepCommission * amount) / 100,
          teamlead_commission:
            (customerCommissionRate.teamLeadCommission * amount) / 100,
          manager_commission:
            (customerCommissionRate.managerCommission * amount) / 100,
          team_lead: teamLeadName,
          manager: managerName,
        };
      }
    );

    console.log("Mapped Sales", mappedSales);
    //setSales(mappedSales);
    console.log("User Title", userTitle);
    if (userTitle === "Sales Rep") {
      console.log("IN a special place");
      setSales(mappedSales.filter((row) => row.isr === userName));
    } else {
      setSales(mappedSales);
    }

    setSales(mappedSales);

    const commissionMap = {};

    // Iterate over the data and accumulate the commissions
    mappedSales.forEach((item) => {
      const isr = item.isr;

      // Initialize the ISR if it's not already in the map
      if (!commissionMap[isr]) {
        let commissionData = commissionResults.find((c) => c.repName === isr);
        commissionMap[isr] = {
          repTitle: commissionData.repTitle || "",
          isr_commission: 0,
          teamlead_commission: 0,
          manager_commission: 0,
          total: 0,
        };
      }

      commissionMap[isr].isr_commission += item.isr_commission;
      commissionMap[isr].teamlead_commission += item.teamlead_commission;
      commissionMap[isr].manager_commission += item.manager_commission;
      commissionMap[isr].total += item.amount;
    });

    // Ensure that every ISR from commissionResults is added to commissionMap, even if they have no sales
    commissionResults.forEach((commissionData) => {
      const isr = commissionData.repName;

      if (!commissionMap[isr]) {
        commissionMap[isr] = {
          repTitle: commissionData.repTitle || "",
          isr_commission: 0,
          teamlead_commission: 0,
          manager_commission: 0,
          total: 0,
        };
      }
    });

    // Now, attach the total commissions back to the original data objects
    data.forEach((item) => {
      const isr = item.isr;

      // Attach the accumulated commission values for the ISR
      item.total_commissions = commissionMap[isr];
    });

    console.log("commissionMap", commissionMap);

    const nameToIdMap = {};
    const summary = data.reduce((acc, sale) => {
      const repName = sale.salesrep?.[0]?.text || "Unknown";
      const repId = sale.salesrep?.[0]?.value;

      if (repId) nameToIdMap[repName] = repId;

      if (!acc[repName]) {
        acc[repName] = {
          totalSales: 0,
          isrCommission: 0,
          teamleadCommission: 0,
          managerCommission: 0,
        };
      }

      acc[repName].totalSales += parseFloat(sale.amount) || 0;
      acc[repName].isrCommission += parseFloat(sale.isr_commission) || 0;
      acc[repName].teamleadCommission +=
        parseFloat(sale.teamlead_commission) || 0;
      acc[repName].managerCommission +=
        parseFloat(sale.manager_commission) || 0;

      return acc;
    }, {});

    console.log("SUMMARY", summary);

    const summaryArray = hierarchyData.map((name) => {
      const totalSales = summary[name]?.totalSales || 0;
      const repId = nameToIdMap[name] || null;
      const commissionData = commissionMap[name] || {
        repTitle: "",
        isr_commission: 0,
        teamlead_commission: 0,
        manager_commission: 0,
      };

      const repCommissionAmount = commissionData.isr_commission;
      const teamLeaderOverrideAmount = commissionData.teamlead_commission;
      const managerOverrideAmount = commissionData.manager_commission;

      let totalCommission = repCommissionAmount;

      if (commissionData.repTitle === "Manager") {
        const subordinates = commissionResults.filter(
          (c) => c.managerName === name
        );
        totalCommission += subordinates.reduce((sum, subordinate) => {
          return (
            sum + (commissionMap[subordinate.repName]?.manager_commission || 0)
          );
        }, 0);
      }

      if (commissionData.repTitle === "Team Lead") {
        const salesReps = commissionResults.filter(
          (c) => c.teamLeadName === name
        );
        totalCommission += salesReps.reduce((sum, salesRep) => {
          return (
            sum + (commissionMap[salesRep.repName]?.teamlead_commission || 0)
          );
        }, 0);
      }

      return {
        id: name,
        manager: commissionData.repTitle === "Manager" ? name : "",
        teamLead: commissionData.repTitle === "Team Lead" ? name : "",
        salesRep: commissionData.repTitle === "Sales Rep" ? name : "",
        sold: totalSales.toFixed(2),
        repCommission: repCommissionAmount.toFixed(2),
        teamLeaderOverrideCommission: teamLeaderOverrideAmount.toFixed(2),
        managerOverrideCommission: managerOverrideAmount.toFixed(2),
        total: totalCommission.toFixed(2),
      };
    });

    console.log("Summary Array with All Individuals", summaryArray);
    if (
      userTitle === "Sales Rep" ||
      (userTitle == "Unknown" && !permissions.isAdmin)
    ) {
      setSalesRepSummary(
        summaryArray.filter((rep) => rep.salesRep === userName)
      );
    } else {
      setSalesRepSummary(summaryArray);
    }

    setSalesRepSummary(summaryArray);

    if (permissions.canViewAnyISRCommission) {
      const isrTotalCommission = data
        .reduce((sum, sale) => sum + (parseFloat(sale.formulacurrency) || 0), 0)
        .toFixed(2);
      setTotalCommissionString(`Total ISR Commission: $${isrTotalCommission}`);
    }
  }, [json, authState, permissions.canViewAnyISRCommission]);

  window.document.title = "Commission Report | Suite Sales";

  if (error) return <ErrorMessage error={error} />;

  const summaryColumns = [];

  // Conditionally add the Manager column as the first column
  if (userTitle !== "Unknown" && userTitle !== "Sales Rep") {
    summaryColumns.push({
      field: "manager",
      headerName: "Manager",
      minWidth: 150,
    });
  }

  // Conditionally add the Team Lead column as the second column
  if (userTitle !== "Unknown" && userTitle !== "Sales Rep") {
    summaryColumns.push({
      field: "teamLead",
      headerName: "Team Lead",
      minWidth: 150,
    });
  }

  summaryColumns.push(
    { field: "salesRep", headerName: "Sales Rep", minWidth: 150 },
    { field: "sold", headerName: "Sold", minWidth: 150, ...usdPrice },
    {
      field: "repCommission",
      headerName: "Rep Commission",
      minWidth: 150,
      ...usdPrice,
    }
  );

  // Conditionally add the Manager Override Commission column
  if (
    userTitle !== "Unknown" &&
    userTitle !== "Sales Rep" &&
    userTitle !== "Team Lead"
  ) {
    summaryColumns.push({
      field: "managerOverrideCommission",
      headerName: "Manager Override Commission",
      minWidth: 250,
      ...usdPrice,
    });
  }

  // Conditionally add the Team Leader Override Commission column
  if (
    userTitle !== "Unknown" &&
    userTitle !== "Sales Rep" &&
    userTitle !== "Manager"
  ) {
    summaryColumns.push({
      field: "teamLeaderOverrideCommission",
      headerName: "Team Leader Override Commission",
      minWidth: 300,
      ...usdPrice,
    });
  }

  summaryColumns.push({
    field: "total",
    headerName: "Total",
    minWidth: 150,
    ...usdPrice,
  });

  //console.log("Rendering BaseCommissionReport with endpoint:", endpoint);
  return (
    <>
      <Header header="Commission Totals" className="justify-between" />
      <FullPageGridContainer>
        <DataGridPro
          rows={salesRepSummary}
          columns={summaryColumns}
          disableSelectionOnClick={true}
          loading={isLoading}
          density="standard"
          disableColumnMenu={true}
          autoHeight
          getRowClassName={(params) =>
            params.row.id === "totals" ? "totals-row" : ""
          }
          components={{
            Toolbar: () => (
              <CustomToolbar currEndpoint={currEndpoint} exportButton={true} />
            ),
          }}
        />
        <Header
          header="Invoices and Credit Memos"
          className="justify-between"
        />
        <DataGridPro
          rows={sales}
          disableSelectionOnClick={true}
          columns={columns}
          loading={isLoading}
          density="standard"
          disableColumnMenu={true}
          // sortModel={[
          //   { field: "isr", sort: "asc" },
          //   { field: "closedate", sort: "asc" },
          // ]}
          // getRowId={(row) => row.internalid}
          components={{
            Toolbar: () => (
              <CustomToolbar currEndpoint={currEndpoint} exportButton={true} />
            ),
            Footer: CustomFooter,
          }}
          componentsProps={{
            footer: { totalCommissionString },
          }}
        />
      </FullPageGridContainer>
    </>
  );
};

export default BaseCommissionReport;
